import { Utils } from '@lightningjs/sdk';

/**
 * Load required libraries for Castlabs player
 */
export class CastlabsPlayerLoader {
  /**
   * Ensures Castlabs libraries are loaded
   */
  static ensureResources(): Promise<void> {
    return new Promise<void>(resolve => {
      const castlabsScript = document.head.querySelector('#castlabs-script');

      if (castlabsScript) {
        resolve();
        return;
      }

      CastlabsPlayerLoader.ensureCoreLib().then(() => {
        CastlabsPlayerLoader.ensureMSELib().then(() => {
          CastlabsPlayerLoader.ensureDashLib().then(() => {
            CastlabsPlayerLoader.ensureSmartlib().then(() => {
              CastlabsPlayerLoader.ensureTtmlLib().then(() => {
                CastlabsPlayerLoader.ensureBroadpeakLib().then(() => resolve());
              });
            });
          });
        });
      });
    });
  }

  /**
   * Ensures Castlabs Core library is loaded
   */
  static ensureCoreLib(): Promise<void> {
    return new Promise(resolve => {
      const castlabsScript = document.createElement('script');
      castlabsScript.id = 'castlabs-script';
      castlabsScript.type = 'text/javascript';
      castlabsScript.src = Utils.asset('libs/castlabs/cl.core.js');

      castlabsScript.onload = () => {
        resolve();
      };

      // load css file
      const cssLink = document.createElement('link');
      cssLink.rel = 'stylesheet';
      cssLink.id = 'castlabs-css';
      cssLink.type = 'text/css';
      cssLink.href = Utils.asset('libs/castlabs/clpp.styles.css');
      document.head.appendChild(cssLink);

      document.head.appendChild(castlabsScript);
    });
  }

  /**
   * Ensures Castlabs MSE library is loaded
   */
  static ensureMSELib(): Promise<void> {
    return new Promise(resolve => {
      let mseScript: HTMLScriptElement | null = document.head.querySelector('#castlabs-mse-script');

      if (mseScript) {
        resolve();
        return;
      }

      // dash not loaded yet, import it
      mseScript = document.createElement('script');
      mseScript.id = 'castlabs-mse-script';
      mseScript.type = 'text/javascript';
      mseScript.src = Utils.asset('libs/castlabs/cl.mse.js');

      mseScript.onload = () => {
        resolve();
      };

      document.head.appendChild(mseScript);
    });
  }

  /**
   * Ensures Castlabs Dash library is loaded
   */
  static ensureDashLib(): Promise<void> {
    return new Promise(resolve => {
      let dashScript: HTMLScriptElement | null = document.head.querySelector('#castlabs-dash-script');

      if (dashScript) {
        resolve();
        return;
      }

      // dash not loaded yet, import it
      dashScript = document.createElement('script');
      dashScript.id = 'castlabs-dash-script';
      dashScript.type = 'text/javascript';
      dashScript.src = Utils.asset('libs/castlabs/cl.dash.js');

      dashScript.onload = () => {
        resolve();
      };

      document.head.appendChild(dashScript);
    });
  }

  /**
   * Ensures Castlabs Broadpeak library is loaded
   */
  static ensureBroadpeakLib(): Promise<void> {
    return new Promise(resolve => {
      let broadpeakScript: HTMLScriptElement | null = document.head.querySelector('#castlabs-broadpeak-script');

      if (broadpeakScript) {
        resolve();
        return;
      }

      // broadpeak not loaded yet, import it
      broadpeakScript = document.createElement('script');
      broadpeakScript.id = 'castlabs-broadpeak-script';
      broadpeakScript.type = 'text/javascript';
      broadpeakScript.src = Utils.asset('libs/castlabs/cl.broadpeak.js');

      broadpeakScript.onload = () => {
        resolve();
      };

      document.head.appendChild(broadpeakScript);
    });
  }

  /**
   * Ensures Smartlib library is loaded
   */
  static ensureSmartlib(): Promise<void> {
    return new Promise(resolve => {
      let SmartlibScript: HTMLScriptElement | null = document.head.querySelector('#smartlib-script');

      if (SmartlibScript) {
        resolve();
        return;
      }

      // smartlib not loaded yet, import it
      SmartlibScript = document.createElement('script');
      SmartlibScript.id = 'smartlib-script';
      SmartlibScript.type = 'text/javascript';
      SmartlibScript.src = Utils.asset('libs/castlabs/smartlib.js');

      SmartlibScript.onload = () => {
        resolve();
      };

      document.head.appendChild(SmartlibScript);
    });
  }

  /**
   * Ensures ttml library is loaded
   */
  static ensureTtmlLib(): Promise<void> {
    return new Promise(resolve => {
      let ttmlScript: HTMLScriptElement | null = document.head.querySelector('#castlabs-ttml-script');

      if (ttmlScript) {
        resolve();
        return;
      }

      // ttml not loaded yet, import it
      ttmlScript = document.createElement('script');
      ttmlScript.id = 'castlabs-ttml-script';
      ttmlScript.type = 'text/javascript';
      ttmlScript.src = Utils.asset('libs/castlabs/cl.ttml.js');

      ttmlScript.onload = () => {
        resolve();
      };

      document.head.appendChild(ttmlScript);
    });
  }

  static cleanup() {
    const castlabsScript = document.head.querySelector('#castlabs-script');
    if (castlabsScript) castlabsScript.remove();
    const mseScript = document.head.querySelector('#castlabs-mse-script');
    if (mseScript) mseScript.remove();
    const dashScript = document.head.querySelector('#castlabs-dash-script');
    if (dashScript) dashScript.remove();
    const broadpeakScript = document.head.querySelector('#castlabs-broadpeak-script');
    if (broadpeakScript) broadpeakScript.remove();
    const SmartlibScript = document.head.querySelector('#smartlib-script');
    if (SmartlibScript) SmartlibScript.remove();
    const ttmlScript = document.head.querySelector('#castlabs-ttml-script');
    if (ttmlScript) ttmlScript.remove();
    const castlabsCss = document.head.querySelector('#castlabs-css');
    if (castlabsCss) castlabsCss.remove();
  }
}
